import { Virtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useRef } from "react";
import { stickyAncestorHeight } from "~/components/build/Show/routes/list/-components/useStickyHeaders";
import { FlatTreeNode } from "~/components/build/Show/routes/list/-components/useTreeNodes";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

export function useScrollToId(
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  nodes: FlatTreeNode[],
  activeStickyIndexRef: React.RefObject<number[]>,
) {
  const { currentLatestJob, currentStep } = useCurrentJobOrStep();
  const params = useQueryParams();
  const scrollRankRef = useRef<number>(undefined);

  /**
   * Scroll element into view if it's not currently visible.
   *
   * `rank` is the target's position in the caller's preference order (lower is
   * more specific). Once a target has been scrolled to, later calls only
   * scroll again for a more specific target — so a selected retry attempt's
   * own row, which may render only after its chain has been fetched, upgrades
   * an earlier scroll to the parent job or step row.
   */
  const scrollToId = useCallback(
    (id: string, rank: number): boolean => {
      // Already scrolled to this or a more specific target for this selection.
      if (scrollRankRef.current !== undefined && scrollRankRef.current <= rank) {
        return true;
      }

      if (!id || !rowVirtualizer?.scrollRect || !rowVirtualizer.scrollElement) {
        return false;
      }

      const virtualItem = rowVirtualizer.getVirtualItems().find((item) => item.key === id);
      const scrollTop = rowVirtualizer.scrollElement.scrollTop;
      const containerHeight = rowVirtualizer.scrollRect.height;
      const index = nodes.findIndex((node) => node.id === id);

      if (index === -1) {
        return false;
      }

      // Don't scroll if the node is currently rendered as a sticky header,
      // since it's already visible at the top of the scroll container.
      if (activeStickyIndexRef.current?.includes(index)) {
        scrollRankRef.current = rank;
        return true;
      }

      const stickyHeight = stickyAncestorHeight(nodes[index]);

      if (
        !virtualItem ||
        virtualItem.start - stickyHeight < scrollTop ||
        virtualItem.end > scrollTop + containerHeight
      ) {
        // Position the item just below its sticky parent headers.
        // We avoid scrollToIndex with align:"start" because it applies
        // a fixed scrollPaddingStart that doesn't match the node's
        // actual sticky header depth.
        const measuredStart = virtualItem?.start ?? index * nodes[index].height;
        const offset = measuredStart - stickyHeight;

        rowVirtualizer.scrollToOffset(offset);
      }

      scrollRankRef.current = rank;
      return true;
    },
    [rowVirtualizer, nodes],
  );

  useEffect(() => {
    // Reset the scroll rank when the job or step changes.
    scrollRankRef.current = undefined;
  }, [params.jid, params.sid]);

  useEffect(() => {
    // Scroll targets, most specific first. With retry history, the selected
    // job's own attempt row is preferred: parallel/matrix latest rows use a
    // synthetic id so they don't collide with the parent job row, and other
    // attempts render under the job's own id. Attempt rows may only render
    // once the retry chain has been fetched, in which case the scroll lands on
    // the latest job or step row first and upgrades when the row appears.
    const targetIds = [
      Features.BuildListJobRetryHistory && params.jid ? `${params.jid}-latest-attempt` : undefined,
      Features.BuildListJobRetryHistory && params.jid ? params.jid : undefined,
      currentLatestJob?.id,
      currentStep?.uuid,
    ];

    for (let rank = 0; rank < targetIds.length; rank++) {
      const id = targetIds[rank];
      if (id && scrollToId(id, rank)) {
        return;
      }
    }
  }, [params.jid, currentLatestJob, currentStep, scrollToId]);
}
